import React, {useCallback} from 'react';
import {StripeError} from '@stripe/stripe-js';
import {useElements, useStripe} from '@stripe/react-stripe-js';

const PaymentErrorMessage = 'Unable to confirm payment. ';
const StripeIncompleteCode = 'incomplete';

function useStripePaymentElement(
  onError: (error: StripeError | string) => void,
  onCancel?: () => void,
) {
  const elements = useElements();
  const stripe = useStripe();
  const isConfirmingRef = React.useRef(false);

  const submitPaymentElement = useCallback(async () => {
    if (!elements) {
      const errorMsg = PaymentErrorMessage + 'Elements instance is missing.';
      onError(errorMsg);
      return false;
    }

    const {error: submitError} = await elements.submit();

    if (submitError) {
      console.error(submitError);
      if (submitError.code !== StripeIncompleteCode) {
        onError(submitError);
      } else {
        onCancel?.();
      }
      return false;
    }

    return true;
  }, [elements, onError, onCancel]);

  const confirmPaymentSetup = React.useCallback(
    async (clientSecret: string, billingDetails?: Record<string, unknown>) => {
      if (isConfirmingRef.current) {
        return;
      }

      if (!stripe) {
        const errorMsg = PaymentErrorMessage + 'Stripe instance is missing.';
        onError(errorMsg);
        return;
      }

      if (!elements) {
        const errorMsg = PaymentErrorMessage + 'Elements instance is missing.';
        onError(errorMsg);
        return;
      }

      isConfirmingRef.current = true;

      try {
        const response = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            payment_method_data: billingDetails
              ? {
                  billing_details: billingDetails,
                }
              : undefined,
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        if (response.error) {
          onError(response.error);
        }

        return response;
      } finally {
        isConfirmingRef.current = false;
      }
    },
    [elements, onError, stripe],
  );

  return {
    submitPaymentElement,
    confirmPaymentSetup,
    isConfirmingRef,
  };
}

export {useStripePaymentElement};
